/**
 * UI-8b Task 6 — TenantLimitsCard.
 *
 * Per-tenant Plan + Limits editor surfaced on TenantDetailPage. Two sections:
 *
 *   1. Plan selector — Dropdown over `usePlans()` items. "Save Plan" PATCHes
 *      the tenant row via `useTenants().update(tenantId, { planId })` and
 *      notifies the parent (`onPlanChange`) so it refetches and the new
 *      planDefaults flow through.
 *   2. Limits grid — 7 InputNumber rows for every tenant_limits column
 *      (UI-8b §0.A revision). Each row's hint text shows either:
 *        - "Inherits plan default: N" when the field is covered by the
 *          tenant's currently-assigned plan (maxAgents +
 *          maxConcurrentCalls only).
 *        - "Default: N" otherwise — the table-level default from
 *          packages/db/src/schema/public.ts.
 *
 * Empty / null InputNumber on save is omitted from the PATCH body so the
 * backend keeps the existing column value (matches backend null-handling).
 *
 * Permission: same as TenantDetailPage (`platform:tenants_manage`); the
 * card does not gate itself.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'

import { usePlans } from '../../hooks/usePlans.js'
import {
  useTenantLimits,
  type TenantLimits,
} from '../../hooks/useTenantLimits.js'
import { useTenants } from '../../hooks/useTenants.js'
import { useToast } from '../system/ToastProvider.js'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The 7 limit columns on `tenant_limits`. Order drives row layout in the
 * grid. Keep in sync with packages/db/src/schema/public.ts (§0.A).
 */
const LIMIT_FIELDS = [
  'maxAgents',
  'maxConcurrentCalls',
  'maxCallsPerMinute',
  'maxDialerOriginatesPerSecond',
  'maxStorageGb',
  'maxApiRequestsPerMinute',
  'maxIvrConcurrent',
] as const

type LimitField = (typeof LIMIT_FIELDS)[number]

/** Human-readable labels per UI-8b spec. */
const LIMIT_LABELS: Record<LimitField, string> = {
  maxAgents: 'Max agents',
  maxConcurrentCalls: 'Max concurrent calls',
  maxCallsPerMinute: 'Max calls per minute',
  maxDialerOriginatesPerSecond: 'Max dialer originates/sec',
  maxStorageGb: 'Max storage (GB)',
  maxApiRequestsPerMinute: 'Max API req/min',
  maxIvrConcurrent: 'Max IVR concurrent',
}

/**
 * Table-level defaults from packages/db/src/schema/public.ts. Used to render
 * the hint text when a field has no plan-default fallback (everything other
 * than maxAgents + maxConcurrentCalls).
 */
const TENANT_LIMIT_DEFAULTS: Record<LimitField, number> = {
  maxAgents: 50,
  maxConcurrentCalls: 100,
  maxCallsPerMinute: 600,
  maxDialerOriginatesPerSecond: 10,
  maxStorageGb: 100,
  maxApiRequestsPerMinute: 6000,
  maxIvrConcurrent: 50,
}

/** Plan-defaults only resolve for these two fields. */
const PLAN_DEFAULTED_FIELDS = new Set<LimitField>([
  'maxAgents',
  'maxConcurrentCalls',
])

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface TenantLimitsCardProps {
  tenantId: string
  tenant: { planId: string | null }
  /** Called after a successful "Save Plan" so the parent can refetch. */
  onPlanChange: () => void
}

export function TenantLimitsCard({
  tenantId,
  tenant,
  onPlanChange,
}: TenantLimitsCardProps): JSX.Element {
  const toast = useToast()
  const { items: plans } = usePlans()
  const { data, loading, save } = useTenantLimits(tenantId)
  const { update: updateTenant } = useTenants()

  const [planId, setPlanId] = useState<string | null>(tenant.planId ?? null)
  const [overrides, setOverrides] = useState<Partial<Record<LimitField, number | null>>>({})
  const [savingPlan, setSavingPlan] = useState(false)
  const [savingLimits, setSavingLimits] = useState(false)

  // Sync local plan-selector state when the tenant prop changes (parent
  // refetched after a successful save).
  useEffect(() => {
    setPlanId(tenant.planId ?? null)
  }, [tenant.planId])

  // Hydrate local overrides from the server response on first load + on every
  // refetch. We populate ALL 7 fields so each InputNumber renders the current
  // effective value rather than blank.
  useEffect(() => {
    if (!data?.limits) return
    const limits = data.limits
    const next: Partial<Record<LimitField, number | null>> = {}
    for (const key of LIMIT_FIELDS) {
      next[key] = limits[key] as number
    }
    setOverrides(next)
  }, [data])

  // --- Save handlers -------------------------------------------------------

  async function handleSavePlan(): Promise<void> {
    setSavingPlan(true)
    try {
      // The tenant-update body omits planId when set to None to keep the
      // type happy under exactOptionalPropertyTypes — the backend allows
      // either a planId or no planId; "None" maps to an explicit null below
      // by relying on the existing UpdateTenantBody contract.
      const body = planId === null ? {} : { planId }
      await updateTenant(tenantId, body)
      toast.success('Plan updated')
      onPlanChange()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save plan')
    } finally {
      setSavingPlan(false)
    }
  }

  async function handleSaveLimits(): Promise<void> {
    setSavingLimits(true)
    try {
      // Build patch body: drop null / undefined fields so the backend keeps
      // the existing value (matches backend null-handling).
      const body: Partial<TenantLimits> = {}
      for (const key of LIMIT_FIELDS) {
        const v = overrides[key]
        if (typeof v === 'number') {
          body[key] = v
        }
      }
      await save(body)
      toast.success('Limits saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save limits')
    } finally {
      setSavingLimits(false)
    }
  }

  // --- Hint text per field -------------------------------------------------

  function hintFor(key: LimitField): string {
    if (PLAN_DEFAULTED_FIELDS.has(key) && data?.planDefaults) {
      const defaults = data.planDefaults
      // The plan-defaults shape only carries maxAgents + maxConcurrentCalls.
      const value =
        key === 'maxAgents' ? defaults.maxAgents : defaults.maxConcurrentCalls
      return `Inherits plan default: ${value}`
    }
    return `Default: ${TENANT_LIMIT_DEFAULTS[key]}`
  }

  // --- Plan dropdown options ----------------------------------------------

  const planOptions = useMemo(
    () => [
      { label: 'None', value: null as string | null },
      ...plans.map((p) => ({ label: p.name, value: p.id as string | null })),
    ],
    [plans],
  )

  return (
    <Card title="Plan + Limits">
      <div className="space-y-6">
        {/* Plan selector ------------------------------------------------- */}
        <section aria-label="Plan selector" className="space-y-2">
          <label className="block text-sm font-medium">Plan</label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Dropdown
                value={planId}
                options={planOptions}
                onChange={(e: { value: string | null }) => {
                  setPlanId(e.value)
                }}
                placeholder="Select a plan"
                aria-label="Plan"
              />
            </div>
            <Button
              label="Save Plan"
              onClick={() => {
                void handleSavePlan()
              }}
              loading={savingPlan}
              disabled={savingPlan}
            />
          </div>
          <p className="text-xs text-gray-500">
            Plans cap maxAgents + maxConcurrentCalls. Other limits fall back
            to global defaults unless overridden below.
          </p>
        </section>

        {/* Limits grid --------------------------------------------------- */}
        <section aria-label="Tenant limit overrides" className="space-y-3">
          <h3 className="text-sm font-medium">Limit overrides</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {LIMIT_FIELDS.map((key) => {
              const inputId = `tenant-limit-${key}`
              return (
                <div key={key} className="flex flex-col gap-1">
                  <label htmlFor={inputId} className="text-sm">
                    {LIMIT_LABELS[key]}
                  </label>
                  <InputNumber
                    inputId={inputId}
                    value={overrides[key] ?? null}
                    onValueChange={(e) => {
                      setOverrides((prev) => ({ ...prev, [key]: e.value }))
                    }}
                    min={1}
                    placeholder={hintFor(key)}
                    disabled={loading}
                  />
                  <small className="text-gray-500">{hintFor(key)}</small>
                </div>
              )
            })}
          </div>
          <div className="flex justify-end">
            <Button
              label="Save Limits"
              onClick={() => {
                void handleSaveLimits()
              }}
              loading={savingLimits}
              disabled={savingLimits || loading}
            />
          </div>
        </section>
      </div>
    </Card>
  )
}

export default TenantLimitsCard
